import { canEditVisitInState } from '@/components/visits/EditVisit/editVisitForm.utils';
import { DialogType, useDialog } from '@/dialogs/DialogManager';
import { FeatureFlag } from '@/types/featureFlag';
import { useFeatureFlagEnabledQuery } from '@/utils/useFeatureFlagEnabled';
import { TypedDocumentNode, gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useCallback } from 'react';

import {
    EditFulfillmentWindowActionQuery,
    EditFulfillmentWindowActionQueryVariables,
} from './useEditFulfillmentWindowAction.gql.types';

export const EditFulfillmentWindowActionFragment = gql`
    fragment EditFulfillmentWindowActionFragment on Visit {
        id
        state
        permissions {
            id
            reschedule
            editWithCompletedOutcome
        }
    }
`;

// Selects only fields a list query already writes to the cached `Visit`, so the cache
// redirect on `Query.visit` resolves it without a network round trip. Non-suspending on
// purpose: this hook is consumed inside table action cells that are not wrapped in a
// Suspense boundary.
const EDIT_FULFILLMENT_WINDOW_ACTION_QUERY: TypedDocumentNode<
    EditFulfillmentWindowActionQuery,
    EditFulfillmentWindowActionQueryVariables
> = gql`
    query EditFulfillmentWindowAction($visitId: UUID!) {
        visit(id: $visitId) {
            ...EditFulfillmentWindowActionFragment
        }
    }
`;

type EditFulfillmentWindowAction = {
    canEditFulfillmentWindow: boolean;
    open: () => void;
};

/**
 * Encapsulates the gating logic for the "Edit Fulfillment Window" action so surfaces only need to
 * pass a `visitId`. The action is available when the feature flag is enabled, the user has the
 * `reschedule` permission, and the visit is editable: not complete, or the user may edit past a
 * completed outcome.
 */
export const useEditFulfillmentWindowAction = (visitId: string): EditFulfillmentWindowAction => {
    const { openDialog } = useDialog();
    const { data } = useQuery(EDIT_FULFILLMENT_WINDOW_ACTION_QUERY, { variables: { visitId } });

    // The surfaces that render this action name the flag in their own flags query, so this
    // resolves from the cache rather than costing a request per row.
    const { enabled: flagEnabled } = useFeatureFlagEnabledQuery(
        FeatureFlag.DASHBOARD_EDIT_VISIT_FULFILLMENT_WINDOW_DIALOG,
    );

    const visit = data?.visit ?? null;
    const canEditFulfillmentWindow =
        flagEnabled &&
        visit !== null &&
        visit.permissions.reschedule &&
        canEditVisitInState({
            state: visit.state,
            canEditWithCompletedOutcome: visit.permissions.editWithCompletedOutcome,
        });

    const open = useCallback(() => {
        openDialog(DialogType.EditFulfillmentWindow, { visitId });
    }, [openDialog, visitId]);

    return { canEditFulfillmentWindow, open };
};
